import { useReducer, useEffect } from 'react';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { getDefaultSelectedOptions, getDataModelErrorState } from '../components/car-picker-helpers';
import { useAutoPopulateDropDowns } from './useAutoPopulateDropDowns';
import { carPickerAnalyticsEvent } from '../analytics';

export const useCarPicker = ({
  carPickerReducer, initialState, navParam, pageType, appliedDimensions
}) => {
  const [state, dispatch] = useReducer(carPickerReducer, { ...initialState, navParam, pageType });

  const {
    fitmentOptions,
    selectedOption,
    fitmentVariables,
    optionTypes,
    dynamicOptionTypes,
    filter
  } = state;
  const variables = {
    fitmentType: 'AUTOMOTIVE',
    optionType: 'YEAR',
    filter: {}
  };
  const { data: yearData, loading: yearLoading, error: yearError } = useDataModel('fitmentOptionsV1', { variables });
  useAutoPopulateDropDowns({
    dispatch,
    selectedOption,
    fitmentOptions,
    variables,
    appliedDimensions,
    optionTypes,
    yearData
  });

  useEffect(() => {
    if (yearData) {
      dispatch({
        action: 'SET_OPTIONS',
        value: {
          ...fitmentOptions,
          YEAR: yearData?.fitmentOptionsV1?.options
        }
      });
    }
  }, [yearData]);

  const optionTypesKey = ['browse', 'search'].includes(pageType) ? 'categoryId' : 'itemId';
  const {
    data: optionTypesData,
    loading: optionTypesLoading,
    error: optionTypesError
  } = useDataModel('fitmentOptionsV1', {
    variables: { ...variables, [optionTypesKey]: navParam, optionType: null }
  });

  const updateHeaders = (dynamicOptionsList) => {
    let updatedHeaders = getDefaultSelectedOptions(dynamicOptionsList);
    dispatch({ action: 'SET_SELECTED_OPTIONS', value: { ...selectedOption, ...updatedHeaders } });
  };

  useEffect(() => {
    if (optionTypesData) {
      const optionsList = optionTypesData?.fitmentOptionsV1?.options;
      const staticKeys = ['year', 'make', 'model'];
      const dynamicOptions = optionsList?.filter(({ id }) => !staticKeys.includes(id));
      dispatch({ action: 'SET_OPTION_TYPES', value: optionsList });
      dispatch({ action: 'SET_DYNAMIC_OPTION_TYPES', value: dynamicOptions });
      updateHeaders(dynamicOptions);
    }
  }, [optionTypesData]);

  const [fitmentRequest, fitmentResponse] = useLazyDataModel('fitmentOptionsV1', {
    ssr: false,
    variables
  });

  useEffect(() => {
    if (fitmentVariables?.variables) {
      fitmentRequest(fitmentVariables);
    }
  }, [fitmentVariables]);

  useEffect(() => {
    if (fitmentResponse?.data?.fitmentOptionsV1) {
      const dropDownOptionKey = fitmentResponse?.variables?.optionType;
      const dropDownOptions = fitmentResponse?.data?.fitmentOptionsV1?.options;

      if (dropDownOptions.length === 1) {
        const { id, value, option } = dropDownOptions[0];
        dispatch({
          action: 'SET_SELECTED_OPTIONS',
          value: { ...selectedOption, [dropDownOptionKey]: { id, displayValue: value, option } }
        });

        const updatedFilters = { ...filter, [option]: id };
        dispatch({ action: 'SET_FILTER', value: updatedFilters });
      }

      dispatch({
        action: 'SET_OPTIONS',
        value: { ...fitmentOptions, [dropDownOptionKey]: dropDownOptions }
      });
    }
  }, [fitmentResponse]);

  const [pipFitmentRequest, pipFitmentResponse] = useLazyDataModel('fitmentItems', { variables: {} });

  useEffect(() => {
    const items = pipFitmentResponse?.data?.fitmentItems;

    if (items) {
      dispatch({ action: 'SET_FITMENT_ITEMS', value: items });

      const { doesFit, hasAlternatives, alternativesUrl } = items?.[0];

      if (doesFit === 'no' && hasAlternatives && alternativesUrl?.length) {
        dispatch({ action: 'SET_REDIRECT_URL', value: alternativesUrl });
      } else {
        dispatch({ action: 'SET_REDIRECT_URL', value: '' });
      }
    }
  }, [pipFitmentResponse]);

  const getStaticDropDownOptions = ({
    id, value, option, currentDropDown, newFilter, index
  }) => {
    let updatedFilters = { ...filter, ...newFilter };
    let selectedOptionsActionValue = {
      ...selectedOption,
      [currentDropDown]: { id, displayValue: value, option }
    };
    const { YEAR, MAKE, MODEL } = fitmentOptions;
    let fitmentOptionsActionValue = { YEAR, MAKE, MODEL };

    if (currentDropDown === 'YEAR') {
      if (selectedOption?.MAKE?.id?.length) {
        const resetOptionsList = optionTypes?.filter((optionType) => (
          optionType.id !== 'year'
        ));
        const defaultSelectedOptions = getDefaultSelectedOptions(resetOptionsList);
        selectedOptionsActionValue = { ...selectedOptionsActionValue, ...defaultSelectedOptions };

        const { year } = updatedFilters;
        updatedFilters = { year };

        fitmentOptionsActionValue = { ...fitmentOptionsActionValue, MODEL: [] };
        dispatch({ action: 'SET_OPTIONS', value: fitmentOptionsActionValue });
      }
    }

    if (currentDropDown === 'MAKE') {
      if (selectedOption?.MODEL?.id?.length) {
        const resetOptionsList = optionTypes?.filter((optionType) => (
          optionType.id !== 'year' && optionType.id !== 'make'
        ));
        const defaultSelectedOptions = getDefaultSelectedOptions(resetOptionsList);
        selectedOptionsActionValue = { ...selectedOptionsActionValue, ...defaultSelectedOptions };

        const { year, make } = updatedFilters;
        updatedFilters = { year, make };

        dispatch({ action: 'SET_OPTIONS', value: fitmentOptionsActionValue });
      }
    }

    if (currentDropDown === 'MODEL') {
      const defaultSelectedOptions = getDefaultSelectedOptions(dynamicOptionTypes);
      selectedOptionsActionValue = { ...selectedOptionsActionValue, ...defaultSelectedOptions };
    }

    // TODO: Should we combine multiple dispatchers into 1, i.e. 'SET_OPTIONS_FILTERS_AND_VARIABLES...'
    dispatch({ action: 'SET_SELECTED_OPTIONS', value: selectedOptionsActionValue });
    dispatch({ action: 'SET_FILTER', value: updatedFilters });

    // Analytics event
    const analyticsData = {
      ...(currentDropDown === 'YEAR' && { year: YEAR[index], yearIndex: index }),
      ...(currentDropDown === 'MAKE' && { make: MAKE[index], makeIndex: index }),
      ...(currentDropDown === 'MODEL' && { model: MODEL[index], modelIndex: index })
    };

    carPickerAnalyticsEvent('dropdown-item-click', analyticsData);
    // End analytics event

    if (currentDropDown !== 'MODEL') {
      const subsequentOptionType = { YEAR: 'MAKE', MAKE: 'MODEL' };

      dispatch({
        action: 'SET_VARIABLES',
        value: {
          variables: {
            fitmentType: 'AUTOMOTIVE',
            optionType: subsequentOptionType[currentDropDown],
            filter: updatedFilters
          }
        }
      });
    }
  };
  const yearState = getDataModelErrorState(yearData, yearLoading, yearError);
  const optionTypesState = getDataModelErrorState(optionTypesData, optionTypesLoading, optionTypesError);
  const errorState = yearState || optionTypesState;
  return [state, dispatch, getStaticDropDownOptions, errorState, pipFitmentRequest];
};